if (!customElements.get('swipeable-gallery')) {
  class SwipeableGallery extends HTMLElement {
    constructor() {
      super();
      this.viewport = null;
      this.track = null;
      this.slides = [];
      this.dots = [];
      this.prevBtn = null;
      this.nextBtn = null;
      this.currentIndex = 0;
      this.isDragging = false;
      this.dragMoved = false;
      this.dragAxisLocked = false;
      this.startX = 0;
      this.startY = 0;
      this.currentTranslate = 0;
      this.dragTranslate = 0;
      this.slideWidth = 0;
      this.cardWrapper = null;
      // Slide animation duration (ms). Default matches the previous hardcoded
      // value; overridden from the theme setting via the data-transition-speed attr.
      this.transitionMs = 150;
      // Desktop-only "show second image on hover". Disabled on touch devices so
      // the existing mobile swipe/gallery behavior stays exactly as-is.
      this.canHover = false;
      // True only while a hover-initiated preview is showing, so leaving the card
      // undoes our own peek but never overrides manual arrow/dot/drag navigation.
      this.hoverActive = false;

      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onCardEnter = this.onCardEnter.bind(this);
      this.onCardLeave = this.onCardLeave.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('.swipeable-gallery__viewport');
      this.track = this.querySelector('.swipeable-gallery__track');
      this.slides = Array.from(this.querySelectorAll('.swipeable-gallery__slide'));
      this.prevBtn = this.querySelector('.swipeable-gallery__arrow--prev');
      this.nextBtn = this.querySelector('.swipeable-gallery__arrow--next');
      this.dots = Array.from(this.querySelectorAll('.swipeable-gallery__dot'));

      if (!this.viewport || !this.track || this.slides.length < 2) return;

      const cardMedia = this.closest('.card__media');
      if (cardMedia) cardMedia.classList.add('card__media--has-gallery');
      const cardInner = this.closest('.card__inner');
      if (cardInner) cardInner.classList.add('card__inner--has-gallery');
      const cardWrapper = this.closest('.card-wrapper');
      if (cardWrapper) cardWrapper.classList.add('card-wrapper--has-gallery');
      this.cardWrapper = cardWrapper;
      const productCardWrapper = this.closest('.product-card-wrapper');
      if (productCardWrapper) productCardWrapper.classList.add('product-card-wrapper--has-gallery');

      const showArrows = this.dataset.showArrows !== 'false';
      const showDots = this.dataset.showDots !== 'false';
      const speed = parseInt(this.dataset.transitionSpeed, 10);
      if (speed >= 0) this.transitionMs = speed;
      if (!showArrows) {
        if (this.prevBtn) this.prevBtn.remove();
        if (this.nextBtn) this.nextBtn.remove();
        this.prevBtn = null;
        this.nextBtn = null;
      } else {
        if (this.prevBtn) this.prevBtn.hidden = false;
        if (this.nextBtn) this.nextBtn.hidden = false;
      }
      if (!showDots) {
        const dotsWrap = this.querySelector('.swipeable-gallery__dots');
        if (dotsWrap) dotsWrap.remove();
        this.dots = [];
      }

      this.classList.add('swipeable-gallery--ready');

      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.addEventListener('touchend', this.onTouchEnd);
      this.addEventListener('touchcancel', this.onTouchEnd);
      this.addEventListener('mousedown', this.onMouseDown);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('click', this.onClickCapture, true);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      // Show the second product image when the card is hovered on desktop.
      // Gated to (a) the "Show second image on hover" setting and (b) hover-capable,
      // fine-pointer devices, so touch swipe behavior is left untouched.
      const hoverSecondImage = this.dataset.hoverSecondImage !== 'false';
      this.canHover =
        hoverSecondImage &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (this.canHover && this.cardWrapper) {
        this.cardWrapper.addEventListener('mouseenter', this.onCardEnter);
        this.cardWrapper.addEventListener('mouseleave', this.onCardLeave);
      }

      window.addEventListener('resize', this.onResize, { passive: true });

      this.measure();
      this.goToIndex(0, true);
    }

    disconnectedCallback() {
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchmove', this.onTouchMove);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.removeEventListener('touchcancel', this.onTouchEnd);
      this.removeEventListener('mousedown', this.onMouseDown);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('click', this.onClickCapture, true);
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      window.removeEventListener('resize', this.onResize);
      if (this.cardWrapper) {
        this.cardWrapper.removeEventListener('mouseenter', this.onCardEnter);
        this.cardWrapper.removeEventListener('mouseleave', this.onCardLeave);
      }
    }

    measure() {
      this.slideWidth = this.viewport.clientWidth;
    }

    onResize() {
      this.measure();
      this.goToIndex(this.currentIndex, true);
    }

    setTrackTransform(translatePx, withTransition) {
      if (!this.track) return;
      this.track.style.transition = withTransition
        ? 'transform ' + this.transitionMs + 'ms cubic-bezier(0.22, 0.61, 0.36, 1)'
        : 'none';
      this.track.style.transform = 'translate3d(' + translatePx + 'px, 0, 0)';
    }

    goToIndex(index, immediate) {
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
      this.currentIndex = clamped;
      this.currentTranslate = -clamped * this.slideWidth;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.setTrackTransform(this.currentTranslate, !immediate && !reduceMotion);
      this.updateActive();
    }

    updateActive() {
      this.dots.forEach((dot, i) => {
        const active = i === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.slides.length - 1;
    }

    beginDrag(x, y) {
      this.measure();
      this.isDragging = true;
      this.dragMoved = false;
      this.dragAxisLocked = false;
      this.startX = x;
      this.startY = y;
      this.dragTranslate = this.currentTranslate;
    }

    updateDrag(x, y, event) {
      if (!this.isDragging) return false;
      const deltaX = x - this.startX;
      const deltaY = y - this.startY;
      if (!this.dragAxisLocked) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return false;
        this.dragAxisLocked = true;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.isDragging = false;
          return false;
        }
      }
      if (event && event.cancelable) event.preventDefault();
      let next = this.currentTranslate + deltaX;
      const min = -(this.slides.length - 1) * this.slideWidth;
      if (next > 0) next = next * 0.3;
      else if (next < min) next = min + (next - min) * 0.3;
      this.dragTranslate = next;
      this.setTrackTransform(next, false);
      if (Math.abs(deltaX) > 6) this.dragMoved = true;
      return true;
    }

    endDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.hoverActive = false;
      const moved = this.dragTranslate - this.currentTranslate;
      const threshold = this.slideWidth * 0.18;
      let target = this.currentIndex;
      if (moved < -threshold) target = this.currentIndex + 1;
      else if (moved > threshold) target = this.currentIndex - 1;
      this.goToIndex(target, false);
      setTimeout(() => { this.dragMoved = false; }, 0);
    }

    onTouchStart(event) {
      if (!event.touches || event.touches.length !== 1) return;
      const t = event.touches[0];
      this.beginDrag(t.clientX, t.clientY);
    }

    onTouchMove(event) {
      if (!event.touches || event.touches.length !== 1) return;
      const t = event.touches[0];
      this.updateDrag(t.clientX, t.clientY, event);
    }

    onTouchEnd() {
      this.endDrag();
    }

    onMouseDown(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this.beginDrag(event.clientX, event.clientY);
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
    }

    onMouseMove(event) {
      this.updateDrag(event.clientX, event.clientY, event);
    }

    onMouseUp() {
      this.endDrag();
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
    }

    onClickCapture(event) {
      if (this.dragMoved) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    onKeyDown(event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.onPrev(); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); this.onNext(); }
    }

    onPrev(event) {
      if (event) { event.preventDefault(); event.stopPropagation(); }
      this.hoverActive = false;
      this.goToIndex(this.currentIndex - 1, false);
    }

    onNext(event) {
      if (event) { event.preventDefault(); event.stopPropagation(); }
      this.hoverActive = false;
      this.goToIndex(this.currentIndex + 1, false);
    }

    onDotClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.hoverActive = false;
      const index = parseInt(event.currentTarget.dataset.slideIndex, 10);
      if (!Number.isNaN(index)) this.goToIndex(index, false);
    }

    onCardEnter() {
      // Desktop hover preview: advance to the second image only when resting on
      // the first slide, so it never fights manual arrow/dot/drag navigation.
      if (!this.canHover || this.isDragging) return;
      if (this.slides.length < 2) return;
      if (this.currentIndex === 0) {
        this.hoverActive = true;
        this.goToIndex(1, false);
      }
    }

    onCardLeave() {
      if (!this.canHover || this.isDragging) return;
      // Only undo a preview we initiated; if the visitor manually navigated,
      // leave their chosen slide in place.
      if (this.hoverActive) {
        this.hoverActive = false;
        this.goToIndex(0, false);
      }
    }
  }

  customElements.define('swipeable-gallery', SwipeableGallery);
}
